import { Link } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from '@/components/ui/button';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import PasswordInput from './password-input';
import { signInSchema, SignInInput } from './schema';

interface Props {
  onSubmit: (data: SignInInput) => void;
}

export default function LoginForm({ onSubmit }: Props) {
  const form = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: zodResolver(signInSchema),
  });

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <FieldGroup>
        <Controller
          control={form.control}
          name="email"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor={field.name}>Work Email</FieldLabel>
              <Input
                {...field}
                aria-invalid={fieldState.invalid}
                autoCapitalize="off"
                autoComplete="email"
                autoCorrect="off"
                autoFocus
                data-testid="email-input"
                id={field.name}
                type="email"
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />
        <Controller
          control={form.control}
          name="password"
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <div className="flex flex-wrap items-center justify-between gap-1">
                <FieldLabel htmlFor={field.name}>Password</FieldLabel>
                <Link
                  className="text-primary text-sm hover:underline"
                  to="/reset-password"
                >
                  Forgot your password?
                </Link>
              </div>
              <PasswordInput
                {...field}
                aria-invalid={fieldState.invalid}
                data-testid="password-input"
                id={field.name}
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />
      </FieldGroup>
      <Button data-testid="submit-login" type="submit">
        Log In
      </Button>
    </form>
  );
}
